import React from 'react';
import { CATEGORY5CONSTANTS } from 'constants/Scope3Constants/category5.constants';
import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import { FieldType } from 'enums/FieldTypeEnums';
import { OrganizationMetaData } from 'models/OrganizationMetaData.ts';
import { FieldsRowProps } from 'shared/components/FieldsRow';
import { allowOnlyNumbers } from 'shared/utils/convertFormData';
import { ReactComponent as DeleteButton } from 'assets/icons/close_enclosed.svg';
import styles from './category5.module.scss';
import { UseFieldArrayRemove } from 'react-hook-form';
import { wasteTreatmentOptions } from './wasteTreatementOptions';

const { INPUT, SELECT, ELEMENT } = FieldType;
const {
  LABELS: {
    TOTAL_WEIGHT_OF_ALL_PRODUCTS,
    TOTAL_WASTE,
    TOTAL_WASTE_WATER,
    WASTE_TREATMENT_METHOD,
    WASTE_TYPE,
    SWITCH_TO_MONTHLY,
    SWITCH_TO_ANNUALLY,
    VIEW_MONTHLY_DATA,
  },
  FIELD_PRODUCTS_TOTAL_WEIGHT,
  FIELD_TOTAL_WASTE,
  FIELD_TOTAL_WASTE_WATER,
  FIELD_TREATMENT_METHOD,
  FIELD_WASTETYPE_ID,
  rowGutter,
  formType,
} = CATEGORY5CONSTANTS;

const {
  productTotalWeight,
  totalWasteProduced,
  totalWasteWater,
  wasteType,
  wasteTreatmentMethod,
} = HelpDeskInputFieldEnum;

interface Category5LayoutDataProps {
  handleToolTipOpen: (fieldName: HelpDeskInputFieldEnum) => () => void;
  isProcessingFacility?: boolean;
  index?: number;
  wasteTypeListMeta?: OrganizationMetaData[];
  remove?: UseFieldArrayRemove;
  arrayLength?: number;
  handleChange?: () => void;
  readOnly?: boolean;
  isYearly?: boolean;
  onSwitch?: () => void;
  onViewMonthly?: () => void;
}
const commonProps = {
  isNullable: true,
  info: true,
  onKeyDown: allowOnlyNumbers,
};

const commonRowProps = {
  rowGutter,
  formType,
};

export const getWeightsData = (
  props: Category5LayoutDataProps
): FieldsRowProps => {
  const {
    handleToolTipOpen,
    isProcessingFacility,
    handleChange,
    readOnly,
  } = props;
  const roDisabled = !!readOnly;
  return {
    data: [
      ...(!!isProcessingFacility
        ? [
            {
              type: INPUT,
              fieldProps: {
                name: FIELD_PRODUCTS_TOTAL_WEIGHT,
                placeholderstring: TOTAL_WEIGHT_OF_ALL_PRODUCTS,
                label: TOTAL_WEIGHT_OF_ALL_PRODUCTS,
                infoClick: handleToolTipOpen(productTotalWeight),
                disabled: roDisabled,
                ...commonProps,
              },
              span: 7,
            },
          ]
        : []),
      {
        type: INPUT,
        fieldProps: {
          name: FIELD_TOTAL_WASTE,
          placeholderstring: TOTAL_WASTE,
          label: TOTAL_WASTE,
          infoClick: handleToolTipOpen(totalWasteProduced),
          onChange: handleChange,
          disabled: roDisabled,
          ...commonProps,
        },
        span: 7,
      },
      {
        type: INPUT,
        fieldProps: {
          name: FIELD_TOTAL_WASTE_WATER,
          placeholderstring: TOTAL_WASTE_WATER,
          label: TOTAL_WASTE_WATER,
          infoClick: handleToolTipOpen(totalWasteWater),
          disabled: roDisabled,
          ...commonProps,
        },
        span: 7,
      },
    ],
    rowClassName: styles.weightsWrapClassname,
    ...commonRowProps,
  };
};

export const getWasteTypeFieldsData = (
  props: Category5LayoutDataProps
): FieldsRowProps => {
  const {
    handleToolTipOpen,
    wasteTypeListMeta,
    index,
    arrayLength,
    remove,
    handleChange,
    readOnly,
    isYearly = true,
    onSwitch,
    onViewMonthly,
  } = props;

  const removeWaste = () => remove?.(index);
  const roDisabled = !!readOnly;
  const showMonthlyActions = !isYearly || !readOnly;
  const selectSpan = isYearly ? 6 : 6;
  const quantitySpan = isYearly ? 4 : 4;
  const monthlyActionsSpan = isYearly ? 6 : 6;
  return {
    data: [
      {
        type: SELECT,
        fieldProps: {
          name: `scopeWasteType.${index}.${FIELD_WASTETYPE_ID}`,
          placeholderstring: WASTE_TYPE,
          label: WASTE_TYPE,
          infoClick: handleToolTipOpen(wasteType),
          options: wasteTypeListMeta,
          info: true,
          onChange: handleChange,
          disabled: roDisabled,
        },
        span: selectSpan,
      },
      {
        type: SELECT,
        fieldProps: {
          name: `scopeWasteType.${index}.${FIELD_TREATMENT_METHOD}`,
          placeholderstring: WASTE_TREATMENT_METHOD,
          label: WASTE_TREATMENT_METHOD,
          infoClick: handleToolTipOpen(wasteTreatmentMethod),
          options: wasteTreatmentOptions,
          info: true,
          onChange: handleChange,
          disabled: roDisabled,
        },
        span: selectSpan,
      },
      {
        type: INPUT,
        fieldProps: {
          name: `scopeWasteType.${index}.${FIELD_TOTAL_WASTE}`,
          placeholderstring: TOTAL_WASTE,
          label: TOTAL_WASTE,
          infoClick: handleToolTipOpen(totalWasteProduced),
          onChange: handleChange,
          disabled: roDisabled || !isYearly,
          ...commonProps,
        },
        span: quantitySpan,
      },
      ...(showMonthlyActions
        ? [
            {
              type: ELEMENT,
              additionalElement: (
                <div className={styles.monthlyActions}>
                  {!isYearly && (
                    <>
                      <p
                        className={styles.viewMonthlyText}
                        onClick={onViewMonthly}>
                        {VIEW_MONTHLY_DATA}
                      </p>
                      {!readOnly && (
                        <span className={styles.pipeSeperator}> | </span>
                      )}
                    </>
                  )}
                  {!readOnly && (
                    <p className={styles.monthlyText} onClick={onSwitch}>
                      {isYearly ? SWITCH_TO_MONTHLY : SWITCH_TO_ANNUALLY}
                    </p>
                  )}
                </div>
              ),
              span: monthlyActionsSpan,
            },
          ]
        : []),
      {
        type: ELEMENT,
        ...(arrayLength && arrayLength > 1 && !readOnly
          ? {
              additionalElement: (
                <div className={styles.closeIcon}>
                  <DeleteButton onClick={removeWaste} />
                </div>
              ),
            }
          : {}),
        className: styles.alignCenter,
      },
    ],
    rowClassName: styles.wrapClassname,
    ...commonRowProps,
  };
};
